import { useMemo } from 'react';

import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';

interface MarkdownRendererProps {
    content: string;
    className?: string;
    imageMap?: Record<string, string>; // Map of {image:id} or {video:id} to actual URLs
}

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content, className = '', imageMap = {} }) => {
    // Process content to replace {image:id} and {video:id} tags with actual markdown image/video syntax
    const processedContent = useMemo(() => {
        if (!content) return '';

        let processed = content;

        // Replace {image:id} tags
        processed = processed.replace(/\{image:([^}]+)\}/g, (_match, id) => {
            const url = imageMap[id];
            if (url) {
                return `![Image](${url})`;
            }
            return `![Image not found: ${id}](#)`;
        });

        // Replace {video:id} tags
        processed = processed.replace(/\{video:([^}]+)\}/g, (_match, id) => {
            const url = imageMap[id];
            if (url) {
                return `<video controls src="${url}" style="max-width: 100%; height: auto;"></video>`;
            }
            return `<p>Video not found: ${id}</p>`;
        });

        // Replace {document:id} tags
        processed = processed.replace(/\{document:([^}]+)\}/g, (_match, id) => {
            const url = imageMap[id];
            if (url) {
                return `[📄 View Document](${url})`;
            }
            return `[Document not found: ${id}](#)`;
        });

        return processed;
    }, [content, imageMap]);

    return (
        <div className={`markdown-content prose dark:prose-invert max-w-none ${className}`}>
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw, rehypeSanitize]}>
                {processedContent}
            </ReactMarkdown>
        </div>
    );
};

export default MarkdownRenderer;
